import { TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';

import { Helpers } from '~commonInfra/utils/Helpers';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { Formatters, Messages } from '~src/codegen/Localize';
import { renderCollectionCardContent } from '~src/Collections/space/CollectionCard';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { interpolateString } from '~src/commonInfra/utils/StringInterpolation';
import {
  DatasourceDefaultImageUrl,
  detailedDatasourceThumbnail,
  hasExtraDatasourceDetail
} from '~src/datasource/components/cards/DatasourceCard';
import { FavoriteToggle } from '~src/favorites/components/favoriteToggle/FavoriteToggle';
import { FavoriteItemType } from '~src/favorites/types/IFavorite';
import { FlowDefaultImageUrl } from '~src/flow/components/cardView/FlowCard';
import {
  MetricCardStateProps,
  MetricDefaultImageUrl,
  renderMetricCardContent
} from '~src/metrics/components/MetricCard';
import { selectMetricDataForContentItem } from '~src/metrics/selectors';
import {
  getAssignedThumbnailImgPath,
  getThumbnailUrlFromDescription,
  PROJECT_THUMBNAIL_HEIGHT,
  ProjectCardShape} from '~src/projects/components/ProjectCard';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { RequestAccessContent, RequestAccessDialog } from '~src/requestAccess/components/RequestAccessDialog';
import { RequestAccessType, RequestAccessTypeValue } from '~src/requestAccess/types/RequestAccessType';
import { CardViewStyles } from '~src/spacesPlatform/components/card/CardViewStyles';
import { CardPopoverWidget, ContentCard, ContentCardProps, getContentUrlForItem } from '~src/spacesPlatform/components/card/ContentCard';
import { Thumbnail, ThumbnailProps } from '~src/spacesPlatform/components/card/Thumbnail';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { User } from '~src/spacesPlatform/components/tooltip/User';
import { ISpaceItemDetailInfo } from '~src/spacesPlatform/models/ISpaceItemDetailInfo';
import { ProfilePictureStyles } from '~src/userManagement/components/styles/ProfilePictureStyles';
import { ProfilePicture } from '~src/userManagement/components/userProfilePicture/ProfilePicture';
import { ViewDefaultImageUrl } from '~src/views/components/ViewCard';
import { WorkbookDefaultImageUrl } from '~src/workbook/components/contentDefinitions/WorkbookCard';

import {
  ISharedWithMeContentInfo,
  ISharedWithMeCustomViewInfo,
  ISharerInfo
} from '../../models/ISharedWithMeContentInfo';
import { SharedViewsButtonStyle } from '../../styles/SharedViewsButtonStyle';
import { SharedViewsButton } from '../sharedWithMe/SharedViewsButton';
import { SharedWithMeTooltip } from '../tooltips/SharedWithMeTooltip';


// MetricCardStateProps contains a metricData property and is made optional because
// SharedWithMeCard can be used for different content types. MetricCardStateProps
// will be defined only when SharedWithMeCard is instantiated for a metric.
export type SharedWithMeCardProps = ContentCardProps&Partial<MetricCardStateProps>;

export class SharedWithMeCard extends ContentCard<SharedWithMeCardProps> {
  public static readonly SHARED_AT_TEST_ID: string = 'sharedAt';
  public static readonly SHARER_TEST_ID: string = 'sharer';
  public static readonly SHARER_SHARED_TEST_ID: string = 'sharer-shared';
  public static readonly CARD_CONTENT_TEST_ID: string = 'shared-with-me-card-content';

  public render() {
    const { urls, addContentFavorite, removeContentFavorite } = this.props;
    const content = this.props.content as ISharedWithMeContentInfo;
    return (
      <div className={CardViewStyles.Card} data-tb-test-id={SharedWithMeCard.CARD_CONTENT_TEST_ID}>
        {this.renderThumbnail(content)}
        <div className={CardViewStyles.ActionRow}>
          <span className={CardViewStyles.ActionRowName} title={content.name}>
            {content.name}
          </span>
          {!content.isAccessDenied &&
          <>
            {this.hasSharedViews(content.isShareActivityIconVisible) &&
            <span>
                <SharedViewsButton className={SharedViewsButtonStyle.Card} urls={urls}
                                   viewId={content.id}/>
              </span>
            }
            <span>
              <CardPopoverWidget
                content={content}
                onTriggerClick={this.handleShowPopover}
                popoverContent={this.renderPopoverContent()}
                title={this.popoverTitle()}
              />
            </span>
            <span>
              <FavoriteToggle
                isTabbable={true}
                isFavorite={content.favorite}
                onAddFavorite={() => addContentFavorite({
                  ...content,
                  contentType: content.contentType as FavoriteItemType
                })}
                onRemoveFavorite={() => removeContentFavorite({
                  ...content,
                  contentType: content.contentType as FavoriteItemType
                })}
              />
            </span>

            {this.renderActionsMenu()}
          </>
          }
        </div>
        <div className={CardViewStyles.SharedWithMeContent}>
          {renderSharerProfilePicture(content.sharer)}
          <div className={CardViewStyles.SharedWithMeSharerAndText}>
            <div>
              {renderSharerNameWithMessage(
                /** sharer */ content.sharer,
                /** urls */ urls,
                /** message */ Messages.shared(),
                /** shownBelowThumbnail */ true,
                /** cv */ content.customView,
              )
              }
            </div>
            {content.sharedAt &&
            <div data-tb-test-id={SharedWithMeCard.SHARED_AT_TEST_ID}>
              {Formatters.formatLongBestFitRelativeTime(content.sharedAt)}
            </div>
            }
          </div>
        </div>
      </div>
    );
  }

  private hasSharedViews = (hasSharedViews: boolean): boolean => {
    return hasSharedViews;
  };

  private renderPopoverContent = () => <SharedWithMeTooltip content={this.props.content} urls={this.props.urls}/>;

  private getCollectionVisilibityFromContentDetails = (content: ISharedWithMeContentInfo): string => {
    if (content.contentType.toLowerCase() === 'collection' && content.contentDetail) {
      return content.contentDetail.visibility || null;
    }
  };

  private openRequestAccessDialog = () => {
    const identifier = this.props.content.contentType === 'collection' ? this.props.content.luid : this.props.content.id;
    const content: RequestAccessContent = {
      identifier,
      type: this.props.content.contentType as RequestAccessType,
      name: this.props.content.name,
    };
    RequestAccessDialog.open(content);
  };

  private handleThumbnailClick = (): void => {
    this.openRequestAccessDialog();
  };

  private handleThumbnailKeyPress = (e: React.KeyboardEvent<HTMLSpanElement>): void => {
    if (e.key === ' ' || e.key === 'Enter') {
      this.openRequestAccessDialog();
      e.preventDefault();
    }
  };

  private renderThumbnail = (content: ISharedWithMeContentInfo) => {
    const isAccessDenied = content.isAccessDenied;
    const ariaLabel = this.thumbnailAltText(isAccessDenied);
    const projectThumbnailProps: Partial<ThumbnailProps> = {
      imageCoverBackground: true,
      customShape: ProjectCardShape,
      customHeight: PROJECT_THUMBNAIL_HEIGHT,
    };

    if ((isAccessDenied && content.contentType in RequestAccessTypeValue) && FeatureFlags.requestAccessDialogEnabled) {
      return (
        <span tabIndex={0} style={{cursor: 'pointer'}} role="button" onKeyDown={this.handleThumbnailKeyPress} onClick={this.handleThumbnailClick}>
          <Thumbnail
            allowDragAndDrop={this.props.allowDragAndDrop}
            aria-label={ariaLabel}
            centeredText={isAccessDenied ? Messages.permissionsRequiredLabel() : undefined}
            decorate={content.contentType === 'workbook'}
            defaultImageUrl={isAccessDenied ? '' : this.mapContentTypeToDefaultImageUrl()}
            hovered={this.props.hovered}
            selected={this.props.selected}
            thumbnailImageUrl={isAccessDenied ? '' : this.thumbnailImageUrl()}
            useBase64EncodedImage={this.useBase64EncodedImage()}
            {...(content.contentType === 'project' && projectThumbnailProps)}
          />
        </span>
      );

    } else if (!isAccessDenied) {
      // metrics thumbnail is a different component
      if (!_.isUndefined(this.props.metricData) && !_.isUndefined(this.props.metricData.data)) {
        return renderMetricCardContent(this.props.metricData, this.props);
      }

      // connected datasource thumbnail is a different component
      if (hasExtraDatasourceDetail(content)) {
        return detailedDatasourceThumbnail(getContentUrlForItem(content, this.props.urls), this.props, ariaLabel);
      }

      if (content.contentType.toLowerCase() === 'collection') {
        // Note: Collection has visibility property (see ICollectionInfo), i.e. content.visibility
        // while it is not available in other content types (see IContentInfo), i.e. content.contentDetails
        // As a workaround for SharedWithMe, getSharedItems API add visibility to contentDetails
        let collectionCardProps: any = this.props;
        collectionCardProps.content.visibility = this.getCollectionVisilibityFromContentDetails(content);
        return renderCollectionCardContent(collectionCardProps);
      }
    }

    return (
      <Thumbnail
        allowDragAndDrop={this.props.allowDragAndDrop}
        aria-label={ariaLabel}
        centeredText={isAccessDenied ? Messages.permissionsRequiredLabel() : undefined}
        decorate={content.contentType === 'workbook'}
        defaultImageUrl={isAccessDenied ? '' : this.mapContentTypeToDefaultImageUrl()}
        hovered={this.props.hovered}
        selected={this.props.selected}
        // if the content has an associated custom view, link to the custom view url instead
        navigateUrl={content.customView ? content.customView.url : getContentUrlForItem(content, this.props.urls)}
        thumbnailImageUrl={isAccessDenied ? '' : this.thumbnailImageUrl()}
        useBase64EncodedImage={this.useBase64EncodedImage()}
        {...(content.contentType === 'project' && projectThumbnailProps)}
      />
    );
  };

  private mapContentTypeToDefaultImageUrl = (): string => {
    switch (this.props.content.contentType.toLowerCase()) {
      case 'view':
        return ViewDefaultImageUrl;
      case 'project':
        return getAssignedThumbnailImgPath(this.props.content.id);
      case 'workbook':
        return WorkbookDefaultImageUrl;
      case 'datasource':
        return DatasourceDefaultImageUrl;
      case 'flow':
        return FlowDefaultImageUrl;
      case 'metric':
        return MetricDefaultImageUrl;
      case 'virtualconnection':
        return PublishedConnections.getPublishedConnectionHeaderIconPath(this.props.content as ISpaceItemDetailInfo<'virtualConnection'>);
      default:
        return '';
    }
  };

  private thumbnailImageUrl = (): string => {
    switch (this.props.content.contentType.toLowerCase()) {
      case 'project':
        return getThumbnailUrlFromDescription(this.props.content.description);
      case 'view':
      case 'workbook':
      case 'datasource':
      case 'flow':
      case 'metric':
      case 'virtualconnection':
      default:
        return this.props.content.thumbnailUrl;
    }
  };

  private thumbnailAltText = (isAccessDenied: boolean): string => {
    const message = isAccessDenied ? Messages.sharedWithMeThumbnailAltTextForAccessDenied : Messages.sharedWithMeThumbnailAltText;
    const contentName = this.props.content.name;
    switch (this.props.content.contentType.toLowerCase()) {
      case 'project':
        return message({CONTENT_TYPE: Messages.project(), CONTENT_NAME: contentName});
      case 'view':
        return message({CONTENT_TYPE: Messages.view(), CONTENT_NAME: contentName});
      case 'workbook':
        return message({CONTENT_TYPE: Messages.workbook(), CONTENT_NAME: contentName});
      case 'datasource':
        return message({CONTENT_TYPE: Messages.datasource(), CONTENT_NAME: contentName});
      case 'flow':
        return message({CONTENT_TYPE: Messages.flow(), CONTENT_NAME: contentName});
      case 'metric':
        return message({CONTENT_TYPE: Messages.metric(), CONTENT_NAME: contentName});
      case 'virtualconnection':
        return message({CONTENT_TYPE: Messages.publishedConnection(), CONTENT_NAME: contentName});
      case 'collection':
        return message({CONTENT_TYPE: Messages.collection(), CONTENT_NAME: contentName});
      default:
        return contentName;
    }
  };

  private popoverTitle = (): string => {
    const contentName = this.props.content.name;
    switch (this.props.content.contentType.toLowerCase()) {
      case 'project':
        return Messages.projectDetails();
      case 'view':
        return Messages.viewTooltipTitle();
      case 'workbook':
        return Messages.workbookDetails();
      case 'datasource':
        return Messages.datasourceDetails();
      case 'flow':
        return Messages.flowTooltipTitle();
      case 'metric':
        return Messages.metricDetails();
      case 'virtualconnection':
        return Messages.publishedConnectionTooltipTitle();
      case 'collection':
        return Messages.collectionDetails();
      default:
        return contentName;
    }
  };

  private useBase64EncodedImage = (): boolean => {
    switch (this.props.content.contentType.toLowerCase()) {
      case 'flow':
        return true;
      case 'project':
      case 'view':
      case 'workbook':
      case 'datasource':
      case 'metric':
      case 'virtualconnection':
      default:
        return false;
    }
  };
}

export function renderSharerProfilePicture(sharer: ISharerInfo) {
  return (
    <div className={CardViewStyles.SharedWithMeProfilePicture}>
      <ProfilePicture
        contentLetters={Helpers.generateAbbrevFromName(sharer.displayName)}
        imageUrl={sharer.isDeleted ? '' : sharer.userImageUrl}
        sizeInPixels={34}
        fontSizeInPixels={ProfilePictureStyles.FontSizes.ExtraSmallIconFontSize}
        shape={ProfilePicture.Shape.Circle}/>
    </div>
  );
}

export function renderSharerNameWithMessage(
  sharer: ISharerInfo,
  urls: SpacesSiteUrls,
  message: string,
  shownBelowThumbnail: boolean,
  cv?: ISharedWithMeCustomViewInfo,
) {
  const sharerName = sharer.isDeleted ? Messages.sharedWithMeRemovedUser({USERNAME: sharer.username}) : sharer.displayName;
  const title = shownBelowThumbnail ? sharerName : undefined;
  const classNameOfInterpolationReplacements = shownBelowThumbnail ? CardViewStyles.SharedWithMeSharerNameSingleLine : undefined;

  let renderSharerName: () => JSX.Element;
  if (shownBelowThumbnail && !sharer.isDeleted) {
    renderSharerName = () => (
      <TextLinkWidget
        className={classNameOfInterpolationReplacements}
        href={urls.userUrl(sharer.username, sharer.domainName)}
        testId={SharedWithMeCard.SHARER_TEST_ID}
        title={title}
      >
        {sharerName}
      </TextLinkWidget>
    );
  } else {
    renderSharerName = () => (
      <span
        className={classNameOfInterpolationReplacements}
        data-tb-test-id={SharedWithMeCard.SHARER_TEST_ID}
        title={title}
      >
        {sharer.isDeleted ? sharerName : <User user={sharer} urls={urls}/>}
      </span>
    );
  }

  return (
    <span
      className={shownBelowThumbnail ? CardViewStyles.SharedWithMeSingleLine : undefined}
      data-tb-test-id={SharedWithMeCard.SHARER_SHARED_TEST_ID}
    >
      {interpolateString(message, {
        USER_NAME: renderSharerName,
        ...(cv && !cv.isHidden) ? {
          VIEW_NAME: () => <span className={classNameOfInterpolationReplacements}>{cv.name}</span>
        } : {}
      })}
    </span>
  );
}

const mapStateToProps: MapStateToProps<MetricCardStateProps> = (state: AppState, ownProps: ContentCardProps) => {
  const metricData = ownProps.content.contentType === 'metric' ? selectMetricDataForContentItem(state, ownProps.content) : undefined;
  return {metricData};
};

export const ConnectedSharedWithMeCard: React.ComponentClass<ContentCardProps> = connect(mapStateToProps, undefined)(SharedWithMeCard);
